import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CanvassingSessionGpsTraceMode } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionGpsTraceQuery } from '../../graphql/queries/canvassingSessionGpsTrace.generated';
import {
  CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE,
  fetchCanvassingSessionGpsTrace,
  type CanvassingSessionFetchImpl,
} from './canvassingSessionApi';
import { isCanvassingSessionId } from './useCanvassingSessionDetail';

export type CanvassingSessionGpsTracePoint = {
  lat: number;
  lng: number;
  capturedAt: string;
  timestamp: number;
};

export type CanvassingSessionGpsTraceBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type CanvassingSessionGpsTracePublication = {
  points: CanvassingSessionGpsTracePoint[];
  totalCount: number;
  sampled: boolean;
  invalidPointCount: number;
  snapshot: string;
  bounds: CanvassingSessionGpsTraceBounds | null;
  fitRevision: number;
};

export type CanvassingSessionGpsTraceError = {
  kind: 'request' | 'malformed';
  message: string;
};

export type CanvassingSessionGpsTraceProgress = {
  loaded: number;
  total: number;
};

export type CanvassingSessionGpsTraceState =
  | { status: 'idle' }
  | { status: 'loading-sampled'; publication: CanvassingSessionGpsTracePublication | null }
  | {
      status: 'sampled';
      publication: CanvassingSessionGpsTracePublication;
      refreshing: boolean;
      error: CanvassingSessionGpsTraceError | null;
    }
  | {
      status: 'loading-full';
      publication: CanvassingSessionGpsTracePublication;
      progress: CanvassingSessionGpsTraceProgress | null;
      refreshingFull: boolean;
    }
  | {
      status: 'full';
      publication: CanvassingSessionGpsTracePublication;
      refreshing: boolean;
      error: CanvassingSessionGpsTraceError | null;
    }
  | { status: 'unavailable' }
  | { status: 'error'; error: CanvassingSessionGpsTraceError };

export type CanvassingSessionGpsTraceRequest = {
  sessionId: string;
  mode: CanvassingSessionGpsTraceMode;
  first: number;
  offset: number;
  snapshot?: string;
};

export type CanvassingSessionGpsTraceApi = {
  fetchTrace: (
    request: CanvassingSessionGpsTraceRequest,
    options: { signal: AbortSignal },
  ) => Promise<CanvassingSessionGpsTraceQuery['canvassingSessionGpsTrace']>;
};

export type UseCanvassingSessionGpsTraceOptions = {
  sessionId: string | undefined;
  fetchImpl?: CanvassingSessionFetchImpl;
  api?: CanvassingSessionGpsTraceApi;
};

export type UseCanvassingSessionGpsTraceResult = {
  state: CanvassingSessionGpsTraceState;
  refresh: () => void;
  loadFull: () => void;
  retry: () => void;
};

type FailureRetry = 'sampled' | 'full' | 'full-refresh';

type TraceActions = {
  refresh: () => void;
  loadFull: () => void;
  retry: () => void;
};

type NormalizedTracePage = {
  points: CanvassingSessionGpsTracePoint[];
  invalidPointCount: number;
  rawPointCount: number;
  totalCount: number;
  sampled: boolean;
  hasNextPage: boolean;
  nextOffset: number | null;
  snapshot: string;
  bounds: CanvassingSessionGpsTraceBounds | null;
};

class TracePageValidationError extends Error {
  constructor() {
    super('The GPS trace response was incomplete.');
  }
}

const toRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? value as Record<string, unknown> : null;

const toTimestamp = (value: unknown): { capturedAt: string; timestamp: number } | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;

  const timestamp = new Date(value).valueOf();
  if (!Number.isFinite(timestamp)) return null;

  return { capturedAt: new Date(timestamp).toISOString(), timestamp };
};

export const normalizeCanvassingSessionGpsTracePoints = (
  points: unknown,
  sourceOffset = 0,
): { points: CanvassingSessionGpsTracePoint[]; invalidPointCount: number } => {
  if (!Array.isArray(points)) throw new TracePageValidationError();

  let invalidPointCount = 0;
  const normalized = points.flatMap((point, index) => {
    const record = toRecord(point);
    const lat = record?.lat;
    const lng = record?.lng;
    const capturedAt = toTimestamp(record?.capturedAt);
    if (
      typeof lat !== 'number'
      || !Number.isFinite(lat)
      || lat < -90
      || lat > 90
      || typeof lng !== 'number'
      || !Number.isFinite(lng)
      || lng < -180
      || lng > 180
      || capturedAt === null
    ) {
      invalidPointCount += 1;
      return [];
    }

    return [{ lat, lng, ...capturedAt, sourceIndex: sourceOffset + index }];
  });

  normalized.sort((left, right) =>
    left.timestamp === right.timestamp ? left.sourceIndex - right.sourceIndex : left.timestamp - right.timestamp,
  );

  return {
    points: normalized.map(({ sourceIndex: _sourceIndex, ...point }) => point),
    invalidPointCount,
  };
};

const sortGpsTracePoints = (points: readonly CanvassingSessionGpsTracePoint[]): CanvassingSessionGpsTracePoint[] =>
  points
    .map((point, sourceIndex) => ({ point, sourceIndex }))
    .sort((left, right) =>
      left.point.timestamp === right.point.timestamp
        ? left.sourceIndex - right.sourceIndex
        : left.point.timestamp - right.point.timestamp,
    )
    .map(({ point }) => point);

const normalizeTracePage = (value: unknown, sourceOffset: number): NormalizedTracePage => {
  const result = toRecord(value);
  const pageInfo = toRecord(result?.pageInfo);
  const totalCount = result?.totalCount;
  const sampled = result?.sampled;
  const hasNextPage = pageInfo?.hasNextPage;
  const nextOffset = pageInfo?.nextOffset;
  const snapshot = pageInfo?.snapshot;

  if (
    result === null
    || typeof totalCount !== 'number'
    || !Number.isSafeInteger(totalCount)
    || totalCount < 0
    || typeof sampled !== 'boolean'
    || typeof hasNextPage !== 'boolean'
    || typeof snapshot !== 'string'
    || snapshot.trim().length === 0
  ) {
    throw new TracePageValidationError();
  }

  let normalizedNextOffset: number | null;
  if (hasNextPage) {
    if (typeof nextOffset !== 'number' || !Number.isSafeInteger(nextOffset) || nextOffset <= 0) {
      throw new TracePageValidationError();
    }
    normalizedNextOffset = nextOffset;
  } else if (nextOffset !== null) {
    throw new TracePageValidationError();
  } else {
    normalizedNextOffset = null;
  }

  if (!Object.prototype.hasOwnProperty.call(result, 'bounds')) {
    throw new TracePageValidationError();
  }

  const normalized = normalizeCanvassingSessionGpsTracePoints(result.points, sourceOffset);
  return {
    ...normalized,
    rawPointCount: Array.isArray(result.points) ? result.points.length : 0,
    totalCount,
    sampled,
    hasNextPage,
    nextOffset: normalizedNextOffset,
    snapshot,
    bounds: normalizeServerBounds(result.bounds),
  };
};

const normalizeServerBounds = (value: unknown): CanvassingSessionGpsTraceBounds | null => {
  if (value === null) return null;
  const record = toRecord(value);
  const north = record?.north;
  const south = record?.south;
  const east = record?.east;
  const west = record?.west;
  if (
    typeof north !== 'number'
    || !Number.isFinite(north)
    || north < -90
    || north > 90
    || typeof south !== 'number'
    || !Number.isFinite(south)
    || south < -90
    || south > 90
    || typeof east !== 'number'
    || !Number.isFinite(east)
    || east < -180
    || east > 180
    || typeof west !== 'number'
    || !Number.isFinite(west)
    || west < -180
    || west > 180
    || north < south
    || west > east
  ) {
    throw new TracePageValidationError();
  }
  return { north, south, east, west };
};

const sameServerBounds = (
  left: CanvassingSessionGpsTraceBounds | null,
  right: CanvassingSessionGpsTraceBounds | null,
): boolean => {
  if (left === null || right === null) return left === right;
  return left.north === right.north
    && left.south === right.south
    && left.east === right.east
    && left.west === right.west;
};

const requestError = (): CanvassingSessionGpsTraceError => ({
  kind: 'request',
  message: 'Could not load the GPS trace. Please try again.',
});

const malformedResponseError = (): CanvassingSessionGpsTraceError => ({
  kind: 'malformed',
  message: 'The GPS trace response was incomplete. Please try again.',
});

const traceApiFromFetch = (fetchImpl: CanvassingSessionFetchImpl | undefined): CanvassingSessionGpsTraceApi => ({
  fetchTrace: async (request, { signal }) => {
    const response = await fetchCanvassingSessionGpsTrace({ ...request, signal, fetchImpl });
    return response.canvassingSessionGpsTrace;
  },
});

const isAbort = (controller: AbortController, active: boolean, generation: number, currentGeneration: number): boolean =>
  !active || controller.signal.aborted || generation !== currentGeneration;

export const useCanvassingSessionGpsTrace = ({
  sessionId,
  fetchImpl,
  api: apiOverride,
}: UseCanvassingSessionGpsTraceOptions): UseCanvassingSessionGpsTraceResult => {
  const api = useMemo(
    () => apiOverride ?? traceApiFromFetch(fetchImpl),
    [apiOverride?.fetchTrace, fetchImpl],
  );
  const [state, setState] = useState<CanvassingSessionGpsTraceState>({ status: 'idle' });
  const actionsRef = useRef<TraceActions>({ refresh: () => undefined, loadFull: () => undefined, retry: () => undefined });

  const refresh = useCallback(() => actionsRef.current.refresh(), []);
  const loadFull = useCallback(() => actionsRef.current.loadFull(), []);
  const retry = useCallback(() => actionsRef.current.retry(), []);

  useEffect(() => {
    if (!isCanvassingSessionId(sessionId)) {
      setState({ status: 'idle' });
      actionsRef.current = { refresh: () => undefined, loadFull: () => undefined, retry: () => undefined };
      return undefined;
    }

    let active = true;
    let requestController: AbortController | null = null;
    let requestGeneration = 0;
    let inFlight: 'sampled' | 'full' | null = null;
    let currentMode: 'sampled' | 'full' | null = null;
    let currentPublication: CanvassingSessionGpsTracePublication | null = null;
    let fitRevision = 0;
    let retryKind: FailureRetry = 'sampled';

    const abortActiveRequest = (): void => {
      requestGeneration += 1;
      inFlight = null;
      const controller = requestController;
      requestController = null;
      controller?.abort();
    };

    const hasCurrentRequest = (controller: AbortController, generation: number): boolean =>
      !isAbort(controller, active, generation, requestGeneration);

    const failSample = (error: CanvassingSessionGpsTraceError): void => {
      retryKind = 'sampled';
      if (currentPublication !== null && currentMode === 'sampled') {
        setState({ status: 'sampled', publication: currentPublication, refreshing: false, error });
        return;
      }

      setState({ status: 'error', error });
    };

    const failFull = (error: CanvassingSessionGpsTraceError, refreshingFull: boolean): void => {
      retryKind = refreshingFull ? 'full-refresh' : 'full';
      if (currentPublication === null || currentMode === null) {
        setState({ status: 'error', error });
        return;
      }

      if (currentMode === 'full') {
        setState({ status: 'full', publication: currentPublication, refreshing: false, error });
      } else {
        setState({ status: 'sampled', publication: currentPublication, refreshing: false, error });
      }
    };

    const startSample = (): void => {
      abortActiveRequest();
      const controller = new AbortController();
      const generation = ++requestGeneration;
      requestController = controller;
      inFlight = 'sampled';

      if (currentPublication !== null && currentMode === 'sampled') {
        setState({ status: 'sampled', publication: currentPublication, refreshing: true, error: null });
      } else {
        setState({ status: 'loading-sampled', publication: null });
      }

      void api.fetchTrace({
        sessionId,
        mode: CanvassingSessionGpsTraceMode.Sampled,
        first: CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE,
        offset: 0,
      }, { signal: controller.signal }).then(result => {
        if (!hasCurrentRequest(controller, generation)) return;
        requestController = null;
        inFlight = null;

        if (result === null) {
          if (currentPublication !== null && currentMode === 'sampled') {
            failSample(requestError());
          } else {
            setState({ status: 'unavailable' });
          }
          return;
        }

        try {
          const page = normalizeTracePage(result, 0);
          const isCompleteSampleModeResult = page.sampled === false && page.rawPointCount === page.totalCount;
          const isStrictSample = page.sampled === true && page.rawPointCount < page.totalCount;
          if (
            page.hasNextPage
            || page.rawPointCount > CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE
            || (!isCompleteSampleModeResult && !isStrictSample)
          ) {
            throw new TracePageValidationError();
          }

          const publication: CanvassingSessionGpsTracePublication = {
            points: page.points,
            totalCount: page.totalCount,
            sampled: page.sampled,
            invalidPointCount: page.invalidPointCount,
            snapshot: page.snapshot,
            bounds: page.bounds,
            fitRevision: ++fitRevision,
          };
          currentPublication = publication;
          currentMode = 'sampled';
          setState({ status: 'sampled', publication, refreshing: false, error: null });
        } catch (error) {
          failSample(error instanceof TracePageValidationError ? malformedResponseError() : requestError());
        }
      }).catch(() => {
        if (!hasCurrentRequest(controller, generation)) return;
        requestController = null;
        inFlight = null;
        failSample(requestError());
      });
    };

    const startFull = (refreshingFull: boolean): void => {
      if (currentPublication === null || currentMode === null || inFlight === 'full') return;

      abortActiveRequest();
      const retainedPublication = currentPublication;
      const controller = new AbortController();
      const generation = ++requestGeneration;
      requestController = controller;
      inFlight = 'full';
      setState({ status: 'loading-full', publication: retainedPublication, progress: null, refreshingFull });

      const load = async (): Promise<void> => {
        let offset = 0;
        let snapshot = refreshingFull ? undefined : retainedPublication.snapshot;
        let totalCount: number | null = null;
        let rawLoaded = 0;
        let invalidPointCount = 0;
        let allPoints: CanvassingSessionGpsTracePoint[] = [];
        let bounds: CanvassingSessionGpsTraceBounds | null = null;
        let hasNextPage = true;

        try {
          while (hasNextPage) {
            const result = await api.fetchTrace({
              sessionId,
              mode: CanvassingSessionGpsTraceMode.Full,
              first: CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE,
              offset,
              ...(snapshot === undefined ? {} : { snapshot }),
            }, { signal: controller.signal });
            if (!hasCurrentRequest(controller, generation)) return;
            if (result === null) throw new Error('unavailable');

            const page = normalizeTracePage(result, rawLoaded);
            if (page.sampled || page.rawPointCount > CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE) {
              throw new TracePageValidationError();
            }

            if (totalCount === null) {
              totalCount = page.totalCount;
              bounds = page.bounds;
              if (!refreshingFull && totalCount !== retainedPublication.totalCount) {
                throw new TracePageValidationError();
              }
              snapshot = page.snapshot;
            } else if (
              page.totalCount !== totalCount
              || page.snapshot !== snapshot
              || !sameServerBounds(page.bounds, bounds)
            ) {
              throw new TracePageValidationError();
            }

            if (!refreshingFull && page.snapshot !== retainedPublication.snapshot) {
              throw new TracePageValidationError();
            }

            rawLoaded += page.rawPointCount;
            if (rawLoaded > totalCount) throw new TracePageValidationError();
            invalidPointCount += page.invalidPointCount;
            allPoints = allPoints.concat(page.points);
            setState({
              status: 'loading-full',
              publication: retainedPublication,
              progress: { loaded: rawLoaded, total: totalCount },
              refreshingFull,
            });

            hasNextPage = page.hasNextPage;
            if (hasNextPage) {
              if (page.nextOffset === null || page.nextOffset <= offset || page.nextOffset > totalCount) {
                throw new TracePageValidationError();
              }
              offset = page.nextOffset;
            }
          }

          if (totalCount === null || snapshot === undefined || rawLoaded !== totalCount) {
            throw new TracePageValidationError();
          }

          const publication: CanvassingSessionGpsTracePublication = {
            points: sortGpsTracePoints(allPoints),
            totalCount,
            sampled: false,
            invalidPointCount,
            snapshot,
            bounds,
            fitRevision: ++fitRevision,
          };
          if (!hasCurrentRequest(controller, generation)) return;
          requestController = null;
          inFlight = null;
          currentPublication = publication;
          currentMode = 'full';
          setState({ status: 'full', publication, refreshing: false, error: null });
        } catch (error) {
          if (!hasCurrentRequest(controller, generation)) return;
          requestController = null;
          inFlight = null;
          failFull(error instanceof TracePageValidationError ? malformedResponseError() : requestError(), refreshingFull);
        }
      };

      void load();
    };

    const refreshTrace = (): void => {
      if (currentMode === 'full') {
        startFull(true);
      } else {
        startSample();
      }
    };

    const loadFullTrace = (): void => {
      if (inFlight !== null || currentMode !== 'sampled' || currentPublication?.sampled !== true) return;
      startFull(false);
    };

    const retryTrace = (): void => {
      if (retryKind === 'full') {
        startFull(false);
      } else if (retryKind === 'full-refresh') {
        startFull(true);
      } else {
        startSample();
      }
    };

    actionsRef.current = { refresh: refreshTrace, loadFull: loadFullTrace, retry: retryTrace };
    startSample();

    return () => {
      active = false;
      actionsRef.current = { refresh: () => undefined, loadFull: () => undefined, retry: () => undefined };
      abortActiveRequest();
    };
  }, [api, sessionId]);

  return { state, refresh, loadFull, retry };
};
